import { useDataSource, useSnackbarController, useTranslation } from "@firecms/core";
import type { EntityCollection } from "@firecms/core";
import { Button, CircularProgress } from "@firecms/ui";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  AdminModal,
  adminModalFileInputClass,
} from "../custom/AdminModal";
import {
  ContentImportParseError,
  parseContentImportFile,
  upsertContentItems,
  type ContentImportCollection,
  type ContentImportResult,
} from "../contentImport/index";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import { getModeCollectionIds } from "../workspace/adminMode";
import { useAdminWorkspace } from "../workspace/useAdminWorkspace";

type ImportContentJsonButtonProps = {
  importCollection: ContentImportCollection;
  collection: EntityCollection;
};

export function ImportContentJsonButton({
  importCollection,
  collection,
}: ImportContentJsonButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ContentImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mode } = useAdminWorkspace();
  const workspaceIds = useMemo(() => getModeCollectionIds(mode), [mode]);
  const dataSource = useDataSource();
  const snackbarController = useSnackbarController();
  const { t } = useTranslation();
  const { t: ta } = useAdminTranslation();

  const importReady =
    (mode.kind === "space" && Boolean(workspaceIds.space)) ||
    (mode.kind === "event" && Boolean(workspaceIds.event));

  const handleClose = useCallback(() => {
    if (!loading) {
      setOpen(false);
      setResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [loading]);

  const handleFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      const contentScopeId =
        mode.kind === "event" ? workspaceIds.event : workspaceIds.space;
      if (!file || !importReady || !contentScopeId) {
        return;
      }

      let items;
      try {
        const text = await file.text();
        items = parseContentImportFile(text);
      } catch (error: unknown) {
        const message =
          error instanceof ContentImportParseError
            ? error.message
            : error instanceof Error
              ? error.message
              : ta("common.unknownError");
        snackbarController.open({
          message: ta("contentImport.parseFailed", { message }),
          type: "warning",
        });
        return;
      }

      try {
        setLoading(true);
        setResult(null);
        const importResult = await upsertContentItems({
          dataSource,
          collection,
          importCollection,
          path: collection.path,
          contentScopeId,
          items,
        });
        setResult(importResult);
        snackbarController.open({
          message: ta("contentImport.snackbarSummary", {
            imported: importResult.imported,
            failed: importResult.failed.length,
          }),
          type: importResult.failed.length > 0 ? "warning" : "success",
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : ta("common.unknownError");
        snackbarController.open({
          message: ta("contentImport.importFailed", { message }),
          type: "error",
        });
      } finally {
        setLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [
      collection,
      dataSource,
      importCollection,
      snackbarController,
      importReady,
      ta,
      mode,
      workspaceIds.event,
      workspaceIds.space,
    ]
  );

  if (mode.kind !== "space" && mode.kind !== "event") {
    return null;
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outlined"
        disabled={!importReady}
      >
        {ta("contentImport.button")}
      </Button>

      <AdminModal
        open={open}
        onClose={handleClose}
        ariaLabel={ta("contentImport.ariaLabel")}
        title={ta("contentImport.title", { collection: importCollection })}
        description={ta("contentImport.description")}
        footer={
          <>
            {loading && <CircularProgress size="small" />}
            <Button onClick={handleClose} disabled={loading} variant="outlined">
              {t("close")}
            </Button>
          </>
        }
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          disabled={loading}
          onChange={(e) => void handleFile(e)}
          className={adminModalFileInputClass}
        />

        {result && (
          <div className="mt-4 text-sm">
            <p className="font-medium text-slate-800 dark:text-slate-200">
              {ta("contentImport.summary", {
                imported: result.imported,
                failed: result.failed.length,
              })}
            </p>
            {result.failed.length > 0 && (
              <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-slate-600 dark:text-slate-400">
                {result.failed.map((row) => (
                  <li key={`${row.index}-${row.key}`}>
                    {ta("contentImport.rowError", {
                      key: row.key,
                      reason: row.reason,
                    })}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </AdminModal>
    </>
  );
}
